import jsPDF from 'jspdf';
import { BaseFilterDefinition, DashboardFilterTypeInd } from '../../../core/services/dashboardsv2.service';
import { GeographicAreaSelectionReducer } from '../../../shared/geographic-area-selection-reducer';

export interface IDashboardExportImage {
    dataUrl: string;
    width: number;
    height: number;
}

export interface IDashboardExportFilter {
    title: string;
    values: string[];
    filterTypeId: DashboardFilterTypeInd;
    options: any[];
    selection: number[];
}

export type DashboardExportPageSize = 'a4' | 'letter';

interface IScaledImageDimensions {
    width: number;
    height: number;
    scale: number;
}

interface IFontFace {
    filename: string;
    // The name used when registering the font with jsPDF.
    name: string;
    weight: FontWeight;
    data: string;
}

type FontWeight = 'normal' | 'bold';

/**
 * A utility to help build dashboard PDF exports with a header, widgets,
 * and a final page consisting of filters.
 */
export class DashboardExportPdfBuilder {
    private readonly _pdf: jsPDF;
    private readonly _pageBorder: number = 20;
    private readonly _pageDimensions: { width: number; height: number };
    private readonly _maxHeaderHeight: number = 96;
    private readonly _filters: IDashboardExportFilter[] = [];
    private readonly _logoPath = '/assets/images/ActivexChangeLogoWhite.png';
    private readonly _fontName: string = 'Roboto';
    private readonly _requiredFonts: Partial<IFontFace>[] = [
        {
            filename: 'Roboto-Regular.ttf',
            name: 'Roboto',
            weight: 'normal',
        },
        {
            filename: 'Roboto-Bold.ttf',
            name: 'Roboto',
            weight: 'bold',
        },
    ];

    // Font sizes in pt
    private readonly _pageNumberFontSize: number = 10;
    private readonly _textFontSize: number = 10;

    private _headerImage: IDashboardExportImage | null = null;
    private _widgetImages: IDashboardExportImage[] = [];
    private _headerImageHeight: number = 0;
    private _logoImage: string | undefined;
    private _geographicLevelsFilter: BaseFilterDefinition | undefined;
    private _organisationDataSetName: string | undefined;

    constructor(
        pageSize: DashboardExportPageSize,
        headerImage: IDashboardExportImage,
        widgetImages: IDashboardExportImage[],
        filters: IDashboardExportFilter[],
        geographicLevelsFilter: BaseFilterDefinition | undefined,
        organisationDataSetName: string | undefined
    ) {
        this._geographicLevelsFilter = geographicLevelsFilter;
        this._organisationDataSetName = organisationDataSetName;
        this._pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            compress: true, // Enable compression for smaller file size
            format: pageSize,
            hotfixes: ['px_scaling'],
        });

        this._pdf.setFont(this._fontName, 'normal');
        this._pdf.setLineHeightFactor(1.5);

        this._pageDimensions = {
            width: this._pdf.internal.pageSize.getWidth(),
            height: this._pdf.internal.pageSize.getHeight(),
        };

        this._headerImage = headerImage;
        this._widgetImages = widgetImages;
        this._filters = filters;
    }

    /**
     * Builds the resulting PDF for export.
     * @returns The jsPDF instance.
     */
    public async getPdf(): Promise<jsPDF> {
        await this._loadLogo();
        await this._loadFonts();

        this._headerImageHeight = this._addHeaderImage();
        this._addFooterImage();
        this._addWidgets();
        this._addPage(false);
        this._addFilters();
        this._addPageNumberLabels();

        return this._pdf;
    }

    /**
     * Add widget images to the PDF.
     * @private
     */
    private _addWidgets(): void {
        const maxWidgetWidth = this._pageDimensions.width - 2 * this._pageBorder;

        let nextWidgetImageY = 2 * this._pageBorder + this._headerImageHeight;

        for (let i = 0; i < this._widgetImages.length; i++) {
            const image = this._widgetImages[i];
            const maxHeight = this._pageDimensions.height - nextWidgetImageY - 3 * this._pageBorder;
            const scaledDimensions = this._scaleImage(image.width, image.height, maxWidgetWidth, maxHeight);

            // Take half the value left over after the image is on the page, taking into account
            // the borders, then add the page border back on
            const imageX =
                (this._pageDimensions.width - 2 * this._pageBorder - scaledDimensions.width) / 2 + this._pageBorder;

            this._pdf.addImage(
                image.dataUrl,
                'JPEG',
                imageX,
                nextWidgetImageY,
                scaledDimensions.width,
                scaledDimensions.height,
                undefined,
                'FAST' // Use fast compression algorithm
            );

            // Next widget image should start one border's width away from previous image
            nextWidgetImageY += scaledDimensions.height + this._pageBorder;

            if (i + 1 < this._widgetImages.length) {
                const nextImage = this._widgetImages[i + 1];
                const nextMaxHeight = this._pageDimensions.height - nextWidgetImageY;
                const nextImageScaledDimensions = this._scaleImage(
                    nextImage.width,
                    nextImage.height,
                    maxWidgetWidth,
                    nextMaxHeight
                );

                // Move to the next page if the remaining space is not enough to fit the image on at full size
                if (
                    this._pageDimensions.height - 2 * this._pageBorder - nextWidgetImageY <
                    nextImageScaledDimensions.height
                ) {
                    this._addPage(false);

                    nextWidgetImageY = this._pageBorder;
                }
            }
        }
    }

    /**
     * Add filter names and values, and an optional data set name, to the PDF.
     * @private
     */
    private _addFilters(): void {
        const sectionSpacing = 15;
        const filterNameSpacing = 5;
        const maxWidth = this._pageDimensions.width - 2 * this._pageBorder;
        const titleFontSize = 16;

        let nextYPos = 3 * this._pageBorder;

        this._pdf.setTextColor('#57318c');
        this._pdf.setFont(this._fontName, 'bold');
        this._pdf.setFontSize(titleFontSize);
        this._pdf.text('Filters', this._pageBorder, nextYPos);

        this._pdf.setFontSize(this._textFontSize);
        this._pdf.setTextColor('#000000');

        nextYPos += this._getLineHeight(titleFontSize);

        for (let i = 0; i < this._filters.length; i++) {
            const filter = this._filters[i];
            const filterTextLines = this._mapFilterText(filter, maxWidth);
            const minimumFilterSpace = this._getLineHeight(titleFontSize + this._textFontSize);

            // Start filter on next page if not enough room
            if (nextYPos > this._pageDimensions.height - 2 * this._pageBorder - minimumFilterSpace) {
                this._addPage(false);
                nextYPos = 2 * this._pageBorder;
            }

            // Filter title
            this._pdf.setFont(this._fontName, 'bold');
            this._pdf.text(this._mapFilterTitle(filter), this._pageBorder, nextYPos);

            nextYPos += this._getLineHeight(this._textFontSize) + filterNameSpacing;

            // Filter values
            this._pdf.setFont(this._fontName, 'normal');

            for (let l = 0; l < filterTextLines.length; l++) {
                const line = filterTextLines[l];

                this._pdf.text(line, this._pageBorder, nextYPos);

                nextYPos += this._getLineHeight(this._textFontSize);

                // Continue filter on next page if not enough room
                if (nextYPos > this._pageDimensions.height - 2 * this._pageBorder - minimumFilterSpace) {
                    this._addPage(false);
                    nextYPos = 2 * this._pageBorder;
                }
            }

            nextYPos += sectionSpacing;
        }

        if (this._organisationDataSetName !== undefined) {
            const requiredSpace =
                this._pageDimensions.height - 2 * this._pageBorder - 2 * this._getLineHeight(this._textFontSize);

            // Move to next page if not enough room
            if (nextYPos > requiredSpace) {
                this._addPage(false);
                nextYPos = 2 * this._pageBorder;
            }

            // Data set title
            this._pdf.setFont(this._fontName, 'bold');
            this._pdf.text('Data Set', this._pageBorder, nextYPos);

            nextYPos += this._getLineHeight(this._textFontSize) + filterNameSpacing;

            // Filter values
            this._pdf.setFont(this._fontName, 'normal');
            this._pdf.text(this._organisationDataSetName, this._pageBorder, nextYPos);
        }
    }

    /**
     * Maps filter values to text that can be used in the PDF.
     * @private
     * @param filter The filter to map text for.
     * @param maxWidth The maximum character width of the page.
     */
    private _mapFilterText(filter: IDashboardExportFilter, maxWidth: number): string[] {
        switch (filter.filterTypeId) {
            case DashboardFilterTypeInd.GeographicLevelSelection:
                const maximumLocationsPerLevelShown = 500;
                const geoAreaSelectionReducer = new GeographicAreaSelectionReducer(
                    this._geographicLevelsFilter.options,
                    this._geographicLevelsFilter.selection
                );
                const highestLevelSelections = geoAreaSelectionReducer.getHighestLevelSelections();

                let textLines: string[] = [];

                for (let [level, locations] of highestLevelSelections.nodesByLevel) {
                    if (locations.length === 0) {
                        continue;
                    }

                    if (locations.length > maximumLocationsPerLevelShown) {
                        const cappedLocations = locations.slice(0, maximumLocationsPerLevelShown);
                        const numberRemainingLocations = locations.length - maximumLocationsPerLevelShown;
                        const locationNames = cappedLocations.map((location) => location.name).join(', ');
                        const levelText = `${
                            level.levelName
                        }: ${locationNames} (plus ${numberRemainingLocations} other${
                            numberRemainingLocations === 1 ? '' : 's'
                        })`;

                        textLines = [...textLines, ...this._pdf.splitTextToSize(levelText, maxWidth), ''];

                        continue;
                    }

                    const locationNames = locations.map((location) => location.name).join(', ');
                    const levelText = `${level.levelName}: ${locationNames}`;

                    textLines = [...textLines, ...this._pdf.splitTextToSize(levelText, maxWidth), ''];
                }

                return textLines;
            default:
                const text = filter.values.join(', ');

                return this._pdf.splitTextToSize(text, maxWidth);
        }
    }

    /**
     * Maps filter title used in the PDF.
     * @private
     * @param filter The filter to map the title for.
     */
    private _mapFilterTitle(filter: IDashboardExportFilter): string {
        let title: string;

        switch (filter.filterTypeId) {
            case DashboardFilterTypeInd.GeographicLevelSelection:
                title = 'Geographic Areas';
                break;
            default:
                title = filter.title;
                break;
        }

        return `${title} ${this._mapFilterSelectionCountText(filter)}`;
    }

    private _mapFilterSelectionCountText(filter: IDashboardExportFilter): string {
        let countText = '';
        let optionsText = 'option';

        switch (filter.filterTypeId) {
            // These filter types only have a single selection
            case DashboardFilterTypeInd.GenericRange:
            case DashboardFilterTypeInd.GenericNumber:
                return '';
            case DashboardFilterTypeInd.GeographicLevelSelection:
                // Most granular level is the one with the lowest ID
                const mostGranularOption = filter.options.sort((a, b) => a.levelId - b.levelId)[0];

                countText = `${filter.selection.length}/${mostGranularOption.locations.length}`;
                optionsText = mostGranularOption.levelName;
                break;
            default:
                countText = `${filter.selection.length}/${filter.options.length}`;
                break;
        }

        return `(${countText} ${optionsText}s selected)`;
    }

    /**
     * Gets the vertical height for a line of text in the document.
     * @private
     */
    private _getLineHeight(fontSize: number): number {
        return fontSize * this._pdf.getLineHeightFactor();
    }

    /**
     * Gets the logo as base64 and caches it locally.
     * @private
     */
    private async _loadLogo(): Promise<string> {
        const response = await fetch(this._logoPath);
        const buffer = await response.arrayBuffer();
        const bytes = new Uint8Array(buffer);

        let binary = '';
        bytes.forEach((b) => (binary += String.fromCharCode(b)));

        const result = btoa(binary);

        this._logoImage = result;

        return result;
    }

    /**
     * Adds a new page to the PDF, with a header and a page number.
     * @private
     */
    private _addPage(addHeader: boolean, addFooterImage: boolean = true): void {
        this._pdf.addPage();

        if (addHeader) {
            this._addHeaderImage();
        }

        if (addFooterImage) {
            this._addFooterImage();
        }
    }

    /**
     * Adds the header image to the top of the current page.
     * @private
     * @returns The height of the added header image (not including the page border), to be used
     * for positioning the next page element.
     */
    private _addHeaderImage(): number {
        const scaledDimensions = this._scaleImage(
            this._headerImage.width,
            this._headerImage.height,
            this._pageDimensions.width - 2 * this._pageBorder,
            this._maxHeaderHeight
        );

        this._pdf.addImage(
            this._headerImage.dataUrl,
            'jpeg',
            this._pageBorder,
            this._pageBorder,
            scaledDimensions.width,
            scaledDimensions.height,
            'headerLogo',
            'FAST'
        );

        return scaledDimensions.height;
    }

    /**
     * Adds the AX logo to the bottom of the current page.
     * @private
     */
    private _addFooterImage(): void {
        const scaledDimensions = this._scaleImage(
            357,
            89,
            357,
            // Constrain by height
            25
        );

        this._pdf.addImage(
            this._logoImage,
            'jpeg',
            this._pageDimensions.width - 0.5 * this._pageBorder - scaledDimensions.width,
            this._pageDimensions.height - 0.65 * this._pageBorder - scaledDimensions.height,
            scaledDimensions.width,
            scaledDimensions.height,
            'footerLogo',
            'FAST'
        );
    }

    /**
     * Calculates the dimensions and scale of an image to fit within a given width and height.
     * @private
     * @returns The dimensions.
     */
    private _scaleImage(
        imageWidth: number,
        imageHeight: number,
        maxWidth: number,
        maxHeight: number
    ): IScaledImageDimensions {
        // Ratio of dimension to the maximum
        const widthRatio = maxWidth / imageWidth;
        const heightRatio = maxHeight / imageHeight;

        // Take the smallest ratio of height or width, so the image will always
        // be within the expected dimensions
        const scale = Math.min(widthRatio, heightRatio);

        const scaledWidth = imageWidth * scale;
        const scaledHeight = imageHeight * scale;

        return {
            width: scaledWidth,
            height: scaledHeight,
            scale: scale,
        };
    }

    /**
     * Adds page number labels to the bottom of all pages.
     * @private
     */
    private _addPageNumberLabels(): void {
        const totalPages = this._pdf.getNumberOfPages();

        for (let page = 1; page <= totalPages; page++) {
            this._pdf.setPage(page);

            const labelText = `Page ${page} of ${totalPages}`;

            // A bit of an arbitrary calculation to calculate the width of the field
            // of text based on the current font size
            const textFieldWidth = labelText.length * this._pageNumberFontSize * 0.8;

            const textX = this._pageDimensions.width / 2 - 0.5 * textFieldWidth;
            const textY = this._pageDimensions.height - this._pageBorder;

            this._pdf.setFontSize(this._pageNumberFontSize);
            this._pdf.text(labelText, textX, textY);
        }
    }

    /**
     * Loads the fonts required for the PDF. The default fonts don't have Unicode character support
     * so we need to load some that do.
     * @private
     */
    private async _loadFonts(): Promise<void> {
        for (let font of this._requiredFonts) {
            let response: Response;

            try {
                response = await fetch(`/assets/fonts/${font.filename}`);
            } catch (er) {
                console.warn(`PDF font ${font.filename} failed to load.`, er);

                // If this font failed to load, skip it.
                continue;
            }

            const buffer = await response.arrayBuffer();
            const encodedData = this._arrayBufferToBase64(buffer);

            this._pdf.addFileToVFS(font.filename, encodedData);
            this._pdf.addFont(font.filename, font.name, font.weight);
        }
    }

    /**
     * Converts an array buffer to a base64 string.
     * @param buffer The buffer to be converted.
     * @private
     */
    private _arrayBufferToBase64(buffer: ArrayBuffer): string {
        let binary = '';

        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;

        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        return btoa(binary);
    }
}
